import type { AjoutControle, ChangementTraduction, Invariant } from '@rosetta/shared';
import { reperer, repererChangements, repererPassages } from './controle';
import { type Segment, decouperInvariants } from './invariants';

/**
 * A block of the translated text: plain, an addition of the control, a change the translation
 * declares (#28) or a passage a "Bloquant" point of another indicator is about (the tab "Texte
 * traduit"), with what is inside it.
 */
export type Bloc =
  | { type: 'texte'; segments: Segment[] }
  | {
      type: 'ajout';
      ajout: AjoutControle;
      segments: Segment[];
    }
  | { type: 'changement'; changement: ChangementTraduction; segments: Segment[] }
  | { type: 'passage'; segments: Segment[] };

interface Positionne {
  debut: number;
  segment: Segment;
}

/** The part of the positioned segments that lies between `debut` and `fin`, cut at the edges. */
function decouper(segments: readonly Positionne[], debut: number, fin: number): Segment[] {
  const morceaux: Segment[] = [];
  for (const { debut: d, segment } of segments) {
    const f = d + segment.texte.length;
    const de = Math.max(d, debut);
    const a = Math.min(f, fin);
    if (de >= a) continue;
    morceaux.push({ ...segment, texte: segment.texte.slice(de - d, a - d) });
  }
  return morceaux;
}

interface Zone {
  debut: number;
  fin: number;
  bloc: (segments: Segment[]) => Bloc;
}

type Intervalle = [debut: number, fin: number];

/** What is left of `[debut, fin]` once the characters of the `pris` zones are taken out. */
function soustraire(debut: number, fin: number, pris: readonly Intervalle[]): Intervalle[] {
  let restes: Intervalle[] = [[debut, fin]];
  for (const [p, q] of pris) {
    restes = restes.flatMap(([d, f]): Intervalle[] => {
      if (q <= d || p >= f) return [[d, f]];
      const gardes: Intervalle[] = [];
      if (d < p) gardes.push([d, p]);
      if (q < f) gardes.push([q, f]);
      return gardes;
    });
  }
  return restes;
}

/** Which marks of the translated text are really shown: the ones the legend has a key for. */
export interface MarquesMontrees {
  ajouts: boolean;
  changements: boolean;
  passages: boolean;
}

/**
 * Which kinds of mark `decouperAjouts` shows in a text: only the passages written there as Tahiti
 * quotes them count, the same search as the cut itself. An addition wins over a passage and a
 * passage over a change, so a change or a passage that lies entirely inside a stronger mark is not
 * shown on its own and has no key.
 */
export function marquesMontrees(
  texte: string,
  ajouts: readonly AjoutControle[],
  changements: readonly ChangementTraduction[] = [],
  passages: readonly string[] = [],
): MarquesMontrees {
  const blocs = decouperAjouts(texte, ajouts, null, changements, passages);
  const montre = (type: Bloc['type']) => blocs.some((bloc) => bloc.type === type);
  return {
    ajouts: montre('ajout'),
    changements: montre('changement'),
    passages: montre('passage'),
  };
}

/**
 * Cuts the translated text into plain blocks, the additions of the control (#23), the passages the
 * control blocks on (other than additions) and the changes the translation declares (#28), each
 * block holding its invariants as segments: an invariant inside a block stays marked, inside it
 * (one that straddles the edge of a block is cut there, both halves marked). The characters are
 * the ones of the text, in order.
 *
 * A passage is found only where it is written exactly as Tahiti quotes it (`reperer`,
 * `repererChangements`, `repererPassages`). The marks never stack: an addition wins over a passage
 * the control blocks on, which wins over a change; where they overlap, the characters belong to
 * the stronger one, and the rest of the other (if any) keeps its own mark.
 */
export function decouperAjouts(
  texte: string,
  ajouts: readonly AjoutControle[],
  invariants?: readonly Invariant[] | null,
  changements: readonly ChangementTraduction[] = [],
  passages: readonly string[] = [],
): Bloc[] {
  const segments: Segment[] =
    invariants == null || invariants.length === 0
      ? [{ type: 'texte', texte }]
      : decouperInvariants(texte, invariants);
  let curseur = 0;
  const positionnes: Positionne[] = segments.map((segment) => {
    const positionne = { debut: curseur, segment };
    curseur += segment.texte.length;
    return positionne;
  });

  const reperes = reperer(texte, ajouts);
  const zones: Zone[] = reperes.map(({ debut, fin, ajout }) => {
    return {
      debut,
      fin,
      bloc: (morceaux) => ({
        type: 'ajout',
        ajout,
        segments: morceaux,
      }),
    };
  });
  const prisParLesAjouts: Intervalle[] = reperes.map(({ debut, fin }) => [debut, fin]);
  const prisParLesPassages: Intervalle[] = [];
  for (const { debut, fin } of repererPassages(texte, passages)) {
    for (const [d, f] of soustraire(debut, fin, prisParLesAjouts)) {
      prisParLesPassages.push([d, f]);
      zones.push({
        debut: d,
        fin: f,
        bloc: (morceaux) => ({ type: 'passage', segments: morceaux }),
      });
    }
  }
  for (const { debut, fin, changement } of repererChangements(texte, changements)) {
    // What is left of the change once the stronger marks took their characters.
    for (const [d, f] of soustraire(debut, fin, [...prisParLesAjouts, ...prisParLesPassages])) {
      zones.push({
        debut: d,
        fin: f,
        bloc: (morceaux) => ({ type: 'changement', changement, segments: morceaux }),
      });
    }
  }
  zones.sort((a, b) => a.debut - b.debut);

  const blocs: Bloc[] = [];
  let position = 0;
  for (const { debut, fin, bloc } of zones) {
    if (debut > position) {
      blocs.push({ type: 'texte', segments: decouper(positionnes, position, debut) });
    }
    blocs.push(bloc(decouper(positionnes, debut, fin)));
    position = fin;
  }
  if (position < texte.length) {
    blocs.push({ type: 'texte', segments: decouper(positionnes, position, texte.length) });
  }
  return blocs;
}
